import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IStockIncident, IStockIncidentInput, stockIncidentsAtom, stockIncidentsLoadingAtom } from "./types";

type UseStockIncidents = [IStockIncident[], {
    list: (query?: Record<string, any>) => Promise<IStockIncident[] | null>;
    getById: (id: number) => Promise<IStockIncident | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IStockIncident[] | null>;
    create: (input: IStockIncidentInput) => Promise<IStockIncident | null>;
    update: (id: number, input: IStockIncidentInput) => Promise<IStockIncident | null>;
    remove: (id: number) => Promise<IStockIncident | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useStockIncidents = (): UseStockIncidents => {
    const [items, setItems] = useAtom(stockIncidentsAtom);
    const setLoading = useSetAtom(stockIncidentsLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getStockIncidents(query: $query){ id inventory_item_id trackable_item_id defective_stock_qty defective_unit_qty reason severity notes reported_by created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getStockIncidents);
            return data.getStockIncidents as IStockIncident[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch stock incidents.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getStockIncidentById(id:$id){ id inventory_item_id trackable_item_id defective_stock_qty defective_unit_qty reason severity notes reported_by created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getStockIncidentById as IStockIncident;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedStockIncidents(first:$first,offset:$offset,query:$query){ id } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedStockIncidents as IStockIncident[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IStockIncidentInput) => {
        try { const q = `mutation($input:StockIncidentInput!){ createStockIncident(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createStockIncident as IStockIncident;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IStockIncidentInput) => {
        try { const q = `mutation($id:ID!,$input:StockIncidentInput!){ updateStockIncident(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateStockIncident as IStockIncident;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteStockIncident(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteStockIncident as IStockIncident;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


